import { useAppDispatch, useAppSelector } from "@/config/store";
import { Card, List, Pagination } from "@/components/ui";
import { IEdoQuiz } from "@/shared/model/edo-quiz.model";
import { getQuizzesByAccount } from "@/shared/reducers/entities/edo-quiz.reducer";
import { EntityState } from "@/shared/reducers/reducer.utils";
import { ITEMS_PER_PAGE } from "@/shared/util/pagination.constants";
import { ClipboardList, Plus } from "lucide-react";
import { useEffect, useState } from "react";
import { IPaginationBaseState, Translate } from "react-jhipster";
import DeleteQuiz from "./components/DeleteQuiz";
import EditQuiz from "./components/EditQuiz";
import QuizRow from "./components/QuizRow";

const QuizzesRoute = () => {
  const dispatch = useAppDispatch();
  const { entities: quizzes, loading, totalItems, updateSuccess } =
    useAppSelector<EntityState<IEdoQuiz>>((state) => state.edoQuiz);
  const [quizToEdit, setQuizToEdit] = useState<IEdoQuiz | null>(null);
  const [editQuizDialog, setEditQuizDialog] = useState<boolean>(false);
  const [deleteQuizDialog, setDeleteQuizDialog] = useState<boolean>(false);
  const [paginationState, setPaginationState] = useState<IPaginationBaseState>({
    activePage: 1,
    itemsPerPage: ITEMS_PER_PAGE,
    sort: "updatedAt",
    order: "DESC",
  });

  const handleOpenEditQuizDialog = (quiz: IEdoQuiz | null) => {
    setQuizToEdit(quiz ?? null);
    setEditQuizDialog(true);
  };

  const handleCloseEditQuizDialog = () => {
    setEditQuizDialog(false);
    setQuizToEdit(null);
  };

  const handleOpenDeleteQuizDialog = (quiz: IEdoQuiz) => {
    setQuizToEdit(quiz);
    setDeleteQuizDialog(true);
  };

  const handleCloseDeleteQuizDialog = () => {
    setDeleteQuizDialog(false);
    setQuizToEdit(null);
  };

  const getAllQuizzes = () => {
    dispatch(
      getQuizzesByAccount({
        page: paginationState.activePage,
        size: paginationState.itemsPerPage,
        sort: `${paginationState.sort},${paginationState.order}`,
      })
    );
  };

  const handlePagination = (currentPage: number) =>
    setPaginationState({
      ...paginationState,
      activePage: currentPage,
    });

  const handleRowsPerPage = (rowsPerPage: number) =>
    setPaginationState({
      ...paginationState,
      itemsPerPage: rowsPerPage,
      activePage: 1,
    });

  useEffect(() => {
    getAllQuizzes();
  }, [
    paginationState.activePage,
    paginationState.order,
    paginationState.sort,
    paginationState.itemsPerPage,
  ]);

  useEffect(() => {
    if (updateSuccess) {
      getAllQuizzes();
    }
  }, [updateSuccess]);

  return (
    <>
      <Card className="overflow-hidden rounded-3xl border border-[var(--color-mockup-border)] shadow-card bg-surface p-0">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-b border-[var(--color-mockup-border)] p-4 bg-[var(--color-surface-secondary)]/30">
          <h2 className="text-lg font-bold text-[var(--color-mockup-text)]">Test</h2>
          <button
            type="button"
            onClick={() => handleOpenEditQuizDialog(null)}
            className="flex shrink-0 items-center gap-1.5 rounded-full bg-[var(--color-mockup-teal)] px-4 py-2 text-xs font-bold text-white shadow-md transition-transform hover:scale-105 active:scale-95"
            aria-label="Aggiungi quiz"
          >
            <Plus className="h-4 w-4" />
            Aggiungi test
          </button>
        </div>
        {loading && (
          <div className="flex justify-center py-8">
            <span className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          </div>
        )}
        {quizzes?.length > 0 ? (
          <List className="divide-y divide-[var(--color-mockup-border)]">
            {quizzes.map((quiz) => (
              <QuizRow
                key={quiz.id}
                quiz={quiz}
                openEditDialog={handleOpenEditQuizDialog}
                openDeleteDialog={handleOpenDeleteQuizDialog}
              />
            ))}
          </List>
        ) : (
          !loading && (
            <div className="px-6 py-12 text-center">
              <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                <ClipboardList className="h-5 w-5 text-primary" />
              </div>
              <p className="text-sm font-medium text-[var(--color-mockup-text-mid)]">
                <Translate contentKey="edocendoApp.edoQuiz.home.notFound" />
              </p>
            </div>
          )
        )}
        <Pagination
          count={totalItems}
          page={paginationState.activePage - 1}
          rowsPerPage={paginationState.itemsPerPage}
          onPageChange={(p) => handlePagination(p + 1)}
          onRowsPerPageChange={handleRowsPerPage}
          labelRowsPerPage="Righe per pagina:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} di ${count !== -1 ? count : "more than" + to}`
          }
        />
      </Card>
      <EditQuiz
        open={editQuizDialog}
        quiz={quizToEdit ?? undefined}
        onClose={handleCloseEditQuizDialog}
      />
      <DeleteQuiz
        open={deleteQuizDialog}
        quiz={quizToEdit ?? ({} as IEdoQuiz)}
        onClose={handleCloseDeleteQuizDialog}
      />
    </>
  );
};

export default QuizzesRoute;
